// src/components/RhinoVOIPCallDetailRecords.jsx
import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  Button,
  DateRangeDropdown,
  Dropdown,
  DropdownMenuItem,
  Icon,
  SmartTable,
  UtilityInlineGrid,
} from 'rhinostyle';
import { useGetCdrsQuery } from '../services/VoipService';
import PageLoader from './PageLoader';
import EmptyMessage from './EmptyMessage';
import SmartTableHeader from './SmartTableHeader';

const PAGE_SIZE = 50;

const CDR_HEADERS_CONFIG = {
  caller_id_number: { default: false, direction: -1 },
  destination_number: { default: false, direction: -1 },
  direction: { default: false, direction: -1 },
  status: { default: false, direction: -1 },
  start_stamp: { default: true, direction: -1 },
  duration: { default: false, direction: -1 },
};

const DATE_PICKER_LABELS = [
  { id: 1, label: 'Yesterday', duration: 1 },
  { id: 2, label: 'Last 7 Days', duration: 6 },
  { id: 3, label: 'Last 30 Days', duration: 29 },
  { id: 4, label: 'Last 90 Days', duration: 89 },
  { id: 5, label: 'Last 12 Months', duration: 'year' },
  { id: 6, label: 'Custom Date' },
];

const DIRECTION_OPTIONS = [
  { id: 'inbound', value: 'Inbound' },
  { id: 'outbound', value: 'Outbound' },
  { id: 'local', value: 'Local' },
];

const STATUS_OPTIONS = [
  { id: 'answered', value: 'Answered' },
  { id: 'missed', value: 'Missed' },
  { id: 'voicemail', value: 'Voicemail' },
  { id: 'busy', value: 'Busy' },
  { id: 'no_answer', value: 'No Answer' },
  { id: 'failed', value: 'Failed' },
];

const RhinoVOIPCallDetailRecords = () => {
  const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(2);
  const [selectedDirections, setSelectedDirections] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedFroms, setSelectedFroms] = useState([]);
  const [selectedTos, setSelectedTos] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [closeCounters, setCloseCounters] = useState({ direction: 0, status: 0, from: 0, to: 0 });

  const handleDropdownOpen = (name) => {
    setCloseCounters((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k !== name) next[k] += 1; });
      return next;
    });
  };

  const buildFilters = useCallback(() => {
    const filters = {
      start_stamp: startDate.format('YYYY-MM-DD'),
      end_stamp: endDate.format('YYYY-MM-DD'),
      limit: PAGE_SIZE,
      offset: pageNumber * PAGE_SIZE,
    };
    if (selectedDirections.length > 0) filters.direction = selectedDirections.join(',');
    if (selectedStatuses.length > 0) filters.status = selectedStatuses.join(',');
    return filters;
  }, [startDate, endDate, selectedDirections, selectedStatuses, pageNumber]);

  const { data, isLoading, isFetching } = useGetCdrsQuery(buildFilters());

  const allRecords = Array.isArray(data?.objects) ? data.objects : [];
  const hasMoreData = allRecords.length >= PAGE_SIZE;

  // Derive distinct From/To options from fetched records
  const fromOptions = useMemo(() => {
    const seen = new Set();
    return allRecords.reduce((acc, r) => {
      const num = r.caller_id_number;
      if (num && !seen.has(num)) {
        seen.add(num);
        const name = r.caller_id_name && r.caller_id_name !== num ? `${r.caller_id_name} (${num})` : num;
        acc.push({ id: num, value: name });
      }
      return acc;
    }, []).sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  const toOptions = useMemo(() => {
    const seen = new Set();
    return allRecords.reduce((acc, r) => {
      const num = r.destination_number;
      if (num && !seen.has(num)) {
        seen.add(num);
        const name = r.dest_name && r.dest_name !== num ? `${r.dest_name} (${num})` : num;
        acc.push({ id: num, value: name });
      }
      return acc;
    }, []).sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  // Client-side filter on From/To
  const records = useMemo(() => {
    let filtered = allRecords;
    if (selectedFroms.length > 0) {
      filtered = filtered.filter((r) => selectedFroms.includes(r.caller_id_number));
    }
    if (selectedTos.length > 0) {
      filtered = filtered.filter((r) => selectedTos.includes(r.destination_number));
    }
    return filtered;
  }, [allRecords, selectedFroms, selectedTos]);

  // --- Filter handlers ---

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      setStartDate(moment(date.startDate));
      setEndDate(moment(date.endDate));
      setActiveKey(date.activeKey);
      setPageNumber(0);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();

    if (selectedLabelIndex === 1) {
      toDate = moment().subtract(1, 'days');
    }
    if (duration === 'year') {
      fromDate = moment().subtract(1, 'years').add(1, 'days');
    }
    return { startDate: fromDate, endDate: toDate };
  };

  const toggleDirection = (value) => {
    setSelectedDirections((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleStatus = (value) => {
    setSelectedStatuses((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleFrom = (value) => {
    setSelectedFroms((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const toggleTo = (value) => {
    setSelectedTos((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const clearAllFilters = () => {
    setStartDate(moment().subtract(6, 'days'));
    setEndDate(moment());
    setActiveKey(2);
    setSelectedDirections([]);
    setSelectedStatuses([]);
    setSelectedFroms([]);
    setSelectedTos([]);
    setPageNumber(0);
    setFilterResetKey((prev) => prev + 1);
  };

  // --- Pagination ---

  const handlePagination = (direction) => {
    if (direction === 'next') {
      setPageNumber((prev) => prev + 1);
    } else if (direction === 'previous' && pageNumber > 0) {
      setPageNumber((prev) => prev - 1);
    }
  };

  const isPreviousDisabled = pageNumber === 0;
  const isNextDisabled = !hasMoreData;
  const pageItemIndexFrom = pageNumber * PAGE_SIZE + 1;
  const pageItemIndexTo = pageNumber * PAGE_SIZE + records.length;

  // --- Formatters ---

  function formatDate(iso) {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
    })}`;
  }

  function formatPhone(value) {
    if (!value) return value;
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return value;
  }

  function formatDuration(sec) {
    if (!sec || sec === 0) return '0s';
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  }

  function formatStatus(str) {
    if (!str) return '-';
    return str
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  function formatCaller(record) {
    if (record.caller_id_name && record.caller_id_name !== record.caller_id_number) {
      return `${record.caller_id_name} (${formatPhone(record.caller_id_number)})`;
    }
    return formatPhone(record.caller_id_number) || '-';
  }

  function formatDestination(record) {
    if (record.dest_name && record.dest_name !== record.destination_number) {
      return `${record.dest_name} (${formatPhone(record.destination_number)})`;
    }
    return formatPhone(record.destination_number) || '-';
  }

  // --- SmartTable columns ---

  const [cdrHeadersConfig] = useState(CDR_HEADERS_CONFIG);

  const smartTableColumns = [
    {
      Header: () => (
        <SmartTableHeader headerName="From" sortKey="caller_id_number" sortDirection={cdrHeadersConfig.caller_id_number.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'caller_id_number',
      Cell: ({ original }) => formatCaller(original),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="To" sortKey="destination_number" sortDirection={cdrHeadersConfig.destination_number.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'destination_number',
      Cell: ({ original }) => formatDestination(original),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Direction" sortKey="direction" sortDirection={cdrHeadersConfig.direction.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'direction',
      Cell: ({ value }) => formatStatus(value),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Status" sortKey="status" sortDirection={cdrHeadersConfig.status.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'status',
      Cell: ({ value }) => formatStatus(value),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Start" sortKey="start_stamp" sortDirection={cdrHeadersConfig.start_stamp.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'start_stamp',
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Duration" sortKey="duration" sortDirection={cdrHeadersConfig.duration.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'duration',
      Cell: ({ value }) => formatDuration(value),
    },
  ];

  // --- Filter state checks ---

  const isDirectionSelected = selectedDirections.length > 0;
  const isStatusSelected = selectedStatuses.length > 0;
  const isFromSelected = selectedFroms.length > 0;
  const isToSelected = selectedTos.length > 0;
  const isAnyFilterSelected = isDirectionSelected || isStatusSelected || isFromSelected || isToSelected;

  const directionLabel = isDirectionSelected ? `${selectedDirections.length} Directions Selected` : 'Direction';
  const statusLabel = isStatusSelected ? `${selectedStatuses.length} Statuses Selected` : 'Status';
  const fromLabel = isFromSelected ? `${selectedFroms.length} From Selected` : 'From';
  const toLabel = isToSelected ? `${selectedTos.length} To Selected` : 'To';

  // --- Pagination controls ---

  const renderPaginationControls = () => {
    const previousCaretClass = isPreviousDisabled ? 'pagination__caret--disabled' : '';
    const nextCaretClass = isNextDisabled ? 'pagination__caret--disabled' : '';
    return (
      <div className="u-text-right u-text-small cdr-records__pagination">
        <span className="inbox-pagination__count">{`${pageItemIndexFrom}-${pageItemIndexTo} Items`}</span>
        <Button
          type="link"
          size="small"
          iconOnly
          disabled={isPreviousDisabled}
          onClick={() => handlePagination('previous')}
          className={previousCaretClass}
        >
          <Icon icon="caret-left" />
        </Button>
        <Button
          type="link"
          iconOnly
          size="small"
          disabled={isNextDisabled}
          onClick={() => handlePagination('next')}
          className={nextCaretClass}
        >
          <Icon icon="caret-right" />
        </Button>
      </div>
    );
  };

  // --- Render data ---

  const renderCdrData = () => {
    if (isLoading || isFetching) {
      return <PageLoader />;
    } else if (records.length === 0) {
      return (
        <div className="cdr-records__nologs-wrapper">
          <EmptyMessage section="Call Detail Records" />
        </div>
      );
    }
    return (
      <>
        <SmartTable
          showPagination={false}
          data={records}
          columns={smartTableColumns}
          minRows={0}
          sortable
          striped
          sticky
          pageSize={records.length}
          defaultSorted={[{ id: 'start_stamp', desc: true }]}
        />
        {records.length > 0 && renderPaginationControls()}
      </>
    );
  };

  const isPaginationControlsVisible = records.length > 0 && pageItemIndexFrom > 0;

  return (
    <div className="app-page__container">
      <div className="app-panels">
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header u-flex-direction-column">
              <div className="app-page__header__title cdr-records__title">Call Detail Records</div>
            </div>
            <div className="box responsive-table cdr-records__grid">
              <div className="cdr-records__filter-wrapper">
                <div className="cdr-records__filter-label">
                  FILTER BY:
                  {isAnyFilterSelected && (
                    <Button data-cypress="clearAll" type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
                  )}
                </div>
                <div className="row">
                  <div>
                    <UtilityInlineGrid>
                      <DateRangeDropdown
                        name="CDR Date Picker"
                        startDate={startDate}
                        endDate={endDate}
                        selectDate={selectDate}
                        minDate={moment().subtract(1, 'year')}
                        maxDate={moment()}
                        activeKey={activeKey}
                        position="center"
                        dropdownMenuItems={DATE_PICKER_LABELS}
                        selectDateRange={calculateDateRange}
                        dataCypress="cdrDatePicker"
                        isCustomDate
                      />
                      <Dropdown
                        key={`direction-${filterResetKey}-${closeCounters.direction}`}
                        position="bottom"
                        activeKey={selectedDirections.length === 1 ? selectedDirections[0] : ''}
                        label={directionLabel}
                        type={isDirectionSelected ? 'primary' : 'input'}
                        outlined={!!isDirectionSelected}
                        name="cdrDirection"
                        onClick={() => handleDropdownOpen('direction')}
                        onSelect={toggleDirection}
                        dataCypress="cdrDirection"
                        manualClose
                      >
                        {DIRECTION_OPTIONS.map((item) => (
                          <DropdownMenuItem
                            key={item.id}
                            id={item.id}
                            label={item.value}
                            icon={selectedDirections.includes(item.id) ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                      <Dropdown
                        key={`status-${filterResetKey}-${closeCounters.status}`}
                        position="bottom"
                        activeKey={selectedStatuses.length === 1 ? selectedStatuses[0] : ''}
                        label={statusLabel}
                        type={isStatusSelected ? 'primary' : 'input'}
                        outlined={!!isStatusSelected}
                        name="cdrStatus"
                        onClick={() => handleDropdownOpen('status')}
                        onSelect={toggleStatus}
                        dataCypress="cdrStatus"
                        manualClose
                      >
                        {STATUS_OPTIONS.map((item) => (
                          <DropdownMenuItem
                            key={item.id}
                            id={item.id}
                            label={item.value}
                            icon={selectedStatuses.includes(item.id) ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                      <Dropdown
                        key={`from-${filterResetKey}-${closeCounters.from}`}
                        position="bottom"
                        activeKey={selectedFroms.length === 1 ? selectedFroms[0] : ''}
                        label={fromLabel}
                        type={isFromSelected ? 'primary' : 'input'}
                        outlined={!!isFromSelected}
                        name="cdrFrom"
                        onClick={() => handleDropdownOpen('from')}
                        onSelect={toggleFrom}
                        dataCypress="cdrFrom"
                        manualClose
                      >
                        {fromOptions.map((item) => (
                          <DropdownMenuItem
                            key={item.id}
                            id={item.id}
                            label={item.value}
                            icon={selectedFroms.includes(item.id) ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                      <Dropdown
                        key={`to-${filterResetKey}-${closeCounters.to}`}
                        position="bottom"
                        activeKey={selectedTos.length === 1 ? selectedTos[0] : ''}
                        label={toLabel}
                        type={isToSelected ? 'primary' : 'input'}
                        outlined={!!isToSelected}
                        name="cdrTo"
                        onClick={() => handleDropdownOpen('to')}
                        onSelect={toggleTo}
                        dataCypress="cdrTo"
                        manualClose
                      >
                        {toOptions.map((item) => (
                          <DropdownMenuItem
                            key={item.id}
                            id={item.id}
                            label={item.value}
                            icon={selectedTos.includes(item.id) ? 'checkmark' : ''}
                          />
                        ))}
                      </Dropdown>
                    </UtilityInlineGrid>
                  </div>
                  <div className="cdr-records__pagination-wrapper">
                    {isPaginationControlsVisible && renderPaginationControls()}
                  </div>
                </div>
              </div>
              {renderCdrData()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RhinoVOIPCallDetailRecords;
